// src/components/MapContainer/MapContainer.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "../Header/Header";
import ColombiaMap, { ColombiaMapHandle } from "../ColombiaMap/ColombiaMap";
import SearchForm from "../SearchForm/SearchForm";
import Leyenda from "../Leyenda/Leyenda";
import PopupDetail from "../PopupDetail/PopupDetail";
import colombiaMapService from "../../services/ColombiaMapService";
import type { GeoJsonFeature } from "../../services/ColombiaMapService";
import { useIsMobile } from "../../hooks/useIsMobile";

const MapContainer: React.FC = () => {
  const isMobile = useIsMobile();
  const [showAlert, setShowAlert] = useState<boolean>(false);

  // paneles
  const [isSearchFormOpen, setIsSearchFormOpen] = useState<boolean>(true);
  const [isLegendOpen, setIsLegendOpen] = useState<boolean>(!isMobile);

  // ancho real de la leyenda (para mover el control del mapa)
  const [legendWidth, setLegendWidth] = useState<number>(isMobile ? 250 : 320);

  // ref del mapa
  const mapRef = useRef<ColombiaMapHandle>(null);

  // zoom compartido (mapa ⇄ slider)
  const [zoom, setZoom] = useState<number>(4.6);

  // feature seleccionado para el popup
  const [selectedFeature, setSelectedFeature] = useState<GeoJsonFeature | null>(null);

  // suscripción a alertas
  useEffect(() => {
    const unsubscribe = colombiaMapService.subscribeToShowAlert((result) => {
      if (result) {
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
      }
    });
    return unsubscribe;
  }, []);

  // manejar parámetro id de URL al cargar la página
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    if (id) {
      // Esperar a que los datos del mapa estén cargados antes de seleccionar el item
      const checkAndSelectItem = () => {
        const geojsonData = colombiaMapService.getGeoJsonData();
        if (geojsonData && geojsonData.features.length > 0) {
          colombiaMapService.selectItemById(id);
        } else {
          // Si los datos no están listos, reintentar después de un breve delay
          setTimeout(checkAndSelectItem, 100);
        }
      };
      checkAndSelectItem();
    }
  }, []);

  // ajustar estado inicial según dispositivo
  useEffect(() => {
    setIsSearchFormOpen(true); // Siempre abierto
    setIsLegendOpen(!isMobile);
    setLegendWidth(isMobile ? 250 : 320);
  }, [isMobile]);

  const handleLegendToggle = (isOpen: boolean) => {
    setIsLegendOpen(isOpen);
  };

  // Cerrar popup con ESC
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") setSelectedFeature(null);
  }, []);
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="flex flex-col w-full h-screen overflow-hidden">
      <Header />

      <div className="relative flex-1 w-full">
        <ColombiaMap
          ref={mapRef}
          onZoomChange={(z) =>
            setZoom((prev) => (Math.abs(prev - z) > 0.01 ? z : prev))
          }
          externalZoom={zoom}
          legendCollapsed={!isLegendOpen}
          legendOpenWidthPx={legendWidth} // dinámico
          rightGapCollapsedPx={28} // más aire cuando está colapsada (no pisar botón)
          rightGapExpandedPx={9}
          controlsTopPx={84}          // <--- NUEVO: 64 (top-16) + 20
        />
      </div>

      {/* Capa UI flotante que NO bloquea el canvas */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Leyenda: lado derecho, debajo del header */}
        <div className="absolute right-3 top-16 z-30 pointer-events-none">
          <Leyenda
            minZoom={4.6}
            maxZoom={18}
            defaultZoom={4.6}
            currentZoom={zoom}
            onLegendToggle={handleLegendToggle}
            onLegendWidthChange={(width) => setLegendWidth(width)}
            onZoomChange={(z) => {
              setZoom(z);
              const map = mapRef.current?.getMap();
              if (map) {
                map.easeTo({ center: map.getCenter(), zoom: z, duration: 250 });
              }
            }}
          />
        </div>

        {/* SearchForm: lado izquierdo */}
        <div className="absolute left-3 top-16 z-30 pointer-events-none">
          <SearchForm
            isOpen={isSearchFormOpen}
            onRequestClose={() => setIsSearchFormOpen(!isSearchFormOpen)}
          />
        </div>

        {/* POPUP: habilitar eventos SOLO en el popup */}
        {selectedFeature && (
          <div className="absolute right-3 top-28 z-50 pointer-events-auto">
            <PopupDetail
              feature={selectedFeature}
              onClose={() => setSelectedFeature(null)}
            />
          </div>
        )}
      </div>

      {/* alerta flotante */}
      {showAlert && (
        <div
          className="absolute z-50 rounded-lg bg-white bottom-2 left-1/2 flex items-center justify-between border border-primary transform -translate-x-1/2"
          role="alert"
        >
          <span className="p-2 text-sm">¡Enlace copiado!</span>
        </div>
      )}
    </div>
  );
};

export default MapContainer;
